import React, { useState, useContext } from 'react';

import { useHistory } from 'react-router-dom';
import Switch from '@mui/material/Switch';
import { Context as AuthContext } from '../../../contexts/authContext';
import { MDBox } from '../../MDBox';
import { MDInput } from '../../MDInput';
import { MDTypography } from '../../MDTypography';
import { MDButton } from '../../MDButton';
import { CircularProgress, Divider } from '@mui/material';
import GoogleSignInButton from './GoogleSignInButton';

export default function LoginForm() {
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { signIn, signInWithMicrosoft, errors, isLoading, clearState } = useContext(AuthContext);
  const history = useHistory();

  const onSignIn = async () => {
    if (!email || !password) {
      return;
    }
    if (signIn) {
      try {
        await signIn(email, password);
      } catch (e) {
        history.push('/login-2fa', { email, password });
      }
    }
  };

  const handleMicrosoftLogin = () => {
    if (signInWithMicrosoft) {
      signInWithMicrosoft();
    }
  };

  const onFocus = () => {
    if (clearState) {
      clearState();
    }
  };

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  // FairFare's own sign-in page, the same as the steerage platform's: a light page with a
  // coral glow, the logo and line, then one card. Email and password stay below the single
  // sign-on buttons for accounts that don't use Microsoft or Google.
  return (
    <MDBox
      minHeight="100vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      px={2}
      py={6}
      sx={{ background: 'radial-gradient(900px 620px at 50% 42%, #FBD9D2 0%, #FDEDE9 40%, #FFF9F7 70%, #FFFFFF 100%)' }}
    >
      <MDBox component="img" src="/ff-logo-transparent.png" alt="FairFare" height="96px" width="auto" display="block" />
      <MDBox mt={3.5} mb={5} width="100%" maxWidth="960px" textAlign="center">
        <MDTypography
          component="p"
          fontWeight="bold"
          sx={{ fontSize: 'clamp(22px, 2.3vw, 36px)', lineHeight: 1.3, letterSpacing: '-0.01em', color: '#14171F' }}
        >
          {/* Two lines, broken after the comma; each wraps only on a narrow screen. */}
          <span style={{ display: 'block' }}>
            AI that steers every member to the <span style={{ color: '#C9473A' }}>right care</span>,
          </span>{' '}
          <span style={{ display: 'block' }}>
            and every claim to the <span style={{ color: '#C9473A' }}>right cost</span>.
          </span>
        </MDTypography>
      </MDBox>
      <MDBox
        width="100%"
        maxWidth="440px"
        bgcolor="#fff"
        p={4}
        sx={{ borderRadius: '12px', borderTop: '5px solid #EA6858', boxShadow: '0 24px 60px rgba(20,23,31,.14)' }}
      >
        <MDTypography sx={{ fontSize: '24px', fontWeight: 700, color: '#14171F', mb: 0.75 }}>Sign in</MDTypography>
        <MDTypography variant="body2" sx={{ color: '#475569', mb: 2.75 }}>
          Use your organization&apos;s work account.
        </MDTypography>
        <MDButton
          variant="outlined"
          color="dark"
          size="large"
          fullWidth
          onClick={handleMicrosoftLogin}
          disabled={isLoading}
          sx={{
            height: '52px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#f8fafc' },
          }}
        >
          <MDBox display="flex" alignItems="center">
            <MDBox
              component="img"
              src="https://teracards.blob.core.windows.net/tm-public-assets/microsoft_logo_512px.png"
              alt="Microsoft Logo"
              width="18px"
              mr={1.5}
            />
            Sign in with Microsoft
          </MDBox>
        </MDButton>
        <MDBox mt={1.5} display="flex" justifyContent="center">
          <GoogleSignInButton />
        </MDBox>
        <MDBox component="form" role="form" mt={3}>
          <MDBox mb={2}>
            <Divider>
              <MDTypography variant="caption" color="text" fontWeight="regular">
                or with email and password
              </MDTypography>
            </Divider>
          </MDBox>
          {errors && (
            <MDBox mb={2}>
              <MDTypography variant="caption" color="error">
                Something went wrong please check your email and password
              </MDTypography>
            </MDBox>
          )}
          <MDBox mb={2}>
            <MDInput type="email" label="Email" fullWidth value={email} onChange={(el) => setEmail(el.target.value)} />
          </MDBox>
          <MDBox mb={1}>
            <MDInput type="password" label="Password" fullWidth value={password} onChange={(el) => setPassword(el.target.value)} />
          </MDBox>
          <MDBox display="flex" alignItems="center" ml={-1}>
            <Switch checked={rememberMe} onChange={handleSetRememberMe} />
            <MDTypography
              variant="button"
              fontWeight="regular"
              color="text"
              onClick={handleSetRememberMe}
              sx={{ cursor: 'pointer', userSelect: 'none', ml: -1 }}
            >
              &nbsp;&nbsp;Remember me
            </MDTypography>
          </MDBox>
          <MDBox mt={2}>
            <MDButton
              size="large"
              fullWidth
              onClick={onSignIn}
              disabled={isLoading || !email || !password}
              sx={{
                height: '48px',
                backgroundColor: '#EA6858',
                color: '#fff',
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#C9473A' },
                '&.Mui-disabled': { backgroundColor: '#F4B3AA', color: '#fff' },
              }}
            >
              {isLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'sign in'}
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
